/**
 * LLMBudgetPanel — tenant-level LLM spend governance.
 * Shows daily + monthly utilisation bars (tokens and USD) and lets Owner/Admin
 * edit the caps or disable enforcement entirely.
 *
 * Backend contract:
 *   GET /api/tenants/current/llm-usage  → {budget, usage, utilisation, status}
 *   PUT /api/tenants/current/llm-budget body: {daily_tokens?, daily_usd?, ...}
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatBackendDetail } from '../lib/apiErrors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Bar({ label, pct, used, cap, fmt }) {
  const pctClamped = pct == null ? null : Math.min(100, Math.max(0, pct));
  const color =
    pctClamped == null ? 'var(--text-secondary)' :
    pctClamped >= 90 ? 'var(--danger, #dc2626)' :
    pctClamped >= 70 ? '#f59e0b' : '#059669';
  return (
    <div style={{ marginBottom: 12 }} data-testid={`llm-bar-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
        <span style={{ textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', fontWeight: 600 }}>
          {label}
        </span>
        <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
          {fmt(used)} / {fmt(cap)}{pctClamped != null && ` · ${pctClamped.toFixed(1)}%`}
        </span>
      </div>
      <div style={{
        width: '100%', height: 8, background: 'var(--bg, #f3f4f6)',
        borderRadius: 4, overflow: 'hidden',
      }}>
        <div style={{
          width: pctClamped != null ? `${pctClamped}%` : '0%',
          height: '100%', background: color, transition: 'width 200ms ease',
        }} />
      </div>
    </div>
  );
}

const fmtTok = (n) => (n == null ? '—' : Number(n).toLocaleString());
const fmtUsd = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);

export default function LLMBudgetPanel() {
  const { user, hasPermission } = useAuth();
  const canManage = hasPermission('tenant:update_settings');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true); setError(null); setForbidden(false);
    try {
      const res = await fetch(`${API_URL}/api/tenants/current/llm-usage`);
      // Platform Root has no tenant_id — backend returns 404. Hide the panel
      // in both auth-forbidden (403) and no-tenant (404) cases.
      if (res.status === 403 || res.status === 404) { setForbidden(true); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setData(d);
      setForm({
        daily_tokens: d.budget.daily_tokens ?? '',
        monthly_tokens: d.budget.monthly_tokens ?? '',
        daily_usd: d.budget.daily_usd ?? '',
        monthly_usd: d.budget.monthly_usd ?? '',
        enabled: d.budget.enabled !== false,
      });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    // Platform Root doesn't belong to a tenant → skip fetching entirely.
    if (user?.is_platform_root) { setForbidden(true); setLoading(false); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        daily_tokens: form.daily_tokens === '' ? null : Number(form.daily_tokens),
        monthly_tokens: form.monthly_tokens === '' ? null : Number(form.monthly_tokens),
        daily_usd: form.daily_usd === '' ? null : Number(form.daily_usd),
        monthly_usd: form.monthly_usd === '' ? null : Number(form.monthly_usd),
        enabled: form.enabled,
      };
      const payload = Object.fromEntries(Object.entries(body).filter(([, v]) => v !== null));
      const res = await fetch(`${API_URL}/api/tenants/current/llm-budget`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatBackendDetail(err.detail) || 'Failed to save');
      }
      setEditing(false);
      await load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  if (forbidden) return null; // Operator/Viewer don't see the card at all

  return (
    <div className="panel" style={{ marginBottom: 16 }} data-testid="llm-budget-panel">
      <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>LLM Budget</span>
        {data && (
          <span style={{
            fontSize: 10, padding: '2px 8px', borderRadius: 10, textTransform: 'uppercase',
            letterSpacing: '0.05em',
            background: data.status === 'ok' ? '#dcfce7' : '#fee2e2',
            color: data.status === 'ok' ? '#166534' : '#991b1b',
          }}>
            {data.status === 'ok' ? 'ENFORCED' : 'DISABLED'}
          </span>
        )}
      </div>
      <div className="panel-body">
        {loading && <div style={{ fontSize: 12 }}>Loading budget…</div>}
        {error && <div className="auth-error" data-testid="llm-budget-error">{error}</div>}

        {!loading && data && !editing && (
          <>
            {/* Proactive warning banners so users aren't surprised by budget denials */}
            {(() => {
              const pcts = [
                data.utilisation.daily_tokens_pct,
                data.utilisation.daily_usd_pct,
                data.utilisation.monthly_tokens_pct,
                data.utilisation.monthly_usd_pct,
              ].filter((p) => p != null);
              const max = pcts.length ? Math.max(...pcts) : 0;
              if (max >= 95) {
                return (
                  <div data-testid="llm-budget-banner-critical"
                       style={{ padding: 8, background: '#fee2e2', color: '#991b1b', borderRadius: 4, fontSize: 12, marginBottom: 12 }}>
                    <strong>Budget critical ({max.toFixed(0)}%).</strong> New LLM calls will be refused until usage resets or caps are raised.
                  </div>
                );
              }
              if (max >= 80) {
                return (
                  <div data-testid="llm-budget-banner-warning"
                       style={{ padding: 8, background: '#fef3c7', color: '#92400e', borderRadius: 4, fontSize: 12, marginBottom: 12 }}>
                    <strong>Budget at {max.toFixed(0)}%.</strong> Consider raising caps or pausing scans before hitting the limit.
                  </div>
                );
              }
              return null;
            })()}
            <Bar label="Daily tokens"
                 pct={data.utilisation.daily_tokens_pct}
                 used={data.usage.daily_tokens} cap={data.budget.daily_tokens} fmt={fmtTok} />
            <Bar label="Daily USD"
                 pct={data.utilisation.daily_usd_pct}
                 used={data.usage.daily_usd} cap={data.budget.daily_usd} fmt={fmtUsd} />
            <Bar label="Monthly tokens"
                 pct={data.utilisation.monthly_tokens_pct}
                 used={data.usage.monthly_tokens} cap={data.budget.monthly_tokens} fmt={fmtTok} />
            <Bar label="Monthly USD"
                 pct={data.utilisation.monthly_usd_pct}
                 used={data.usage.monthly_usd} cap={data.budget.monthly_usd} fmt={fmtUsd} />

            {canManage && (
              <button className="btn btn-outline btn-sm" onClick={() => setEditing(true)}
                      data-testid="llm-budget-edit-btn" style={{ marginTop: 8 }}>
                Edit caps
              </button>
            )}
            {!canManage && (
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                Budget caps can only be changed by Owner or Admin.
              </div>
            )}
          </>
        )}

        {!loading && data && editing && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Daily tokens
              <input type="number" min={0} value={form.daily_tokens}
                     onChange={(e) => setForm({ ...form, daily_tokens: e.target.value })}
                     data-testid="llm-budget-daily-tokens"
                     style={{ width: '100%', padding: 6, marginTop: 2, border: '1px solid var(--border)', borderRadius: 4 }} />
            </label>
            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Daily USD
              <input type="number" min={0} step={0.01} value={form.daily_usd}
                     onChange={(e) => setForm({ ...form, daily_usd: e.target.value })}
                     data-testid="llm-budget-daily-usd"
                     style={{ width: '100%', padding: 6, marginTop: 2, border: '1px solid var(--border)', borderRadius: 4 }} />
            </label>
            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Monthly tokens
              <input type="number" min={0} value={form.monthly_tokens}
                     onChange={(e) => setForm({ ...form, monthly_tokens: e.target.value })}
                     data-testid="llm-budget-monthly-tokens"
                     style={{ width: '100%', padding: 6, marginTop: 2, border: '1px solid var(--border)', borderRadius: 4 }} />
            </label>
            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Monthly USD
              <input type="number" min={0} step={0.01} value={form.monthly_usd}
                     onChange={(e) => setForm({ ...form, monthly_usd: e.target.value })}
                     data-testid="llm-budget-monthly-usd"
                     style={{ width: '100%', padding: 6, marginTop: 2, border: '1px solid var(--border)', borderRadius: 4 }} />
            </label>
            <label style={{ gridColumn: 'span 2', display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
              <input type="checkbox" checked={form.enabled}
                     onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                     data-testid="llm-budget-enabled-toggle" />
              Enforcement enabled (turn off only for regression testing)
            </label>
            <div style={{ gridColumn: 'span 2', display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}
                      data-testid="llm-budget-save-btn">
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button className="btn btn-outline btn-sm" onClick={() => { setEditing(false); load(); }}
                      data-testid="llm-budget-cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
